/**
 * WordPress API Service for VNCues
 * Kết nối với WordPress REST API endpoint
 */

import { getIdToken } from './firebaseAuth';

// Configuration from environment variables or window config (for WordPress)
const windowConfig = (window as any).VNCUES_CONFIG || {};

// WordPress Site URL
export const WP_SITE_URL = import.meta.env.VITE_WP_SITE_URL || windowConfig.siteUrl || 'https://vncues.com';

// API Base URLs
export const WP_API_BASE = import.meta.env.VITE_WP_API_BASE 
  || windowConfig.apiBase 
  || `${WP_SITE_URL}/wp-json/vncues/v1`;
export const WP_API_BASE_CORE = import.meta.env.VITE_WP_API_CORE 
  || `${WP_SITE_URL}/wp-json/wp/v2`;

export interface WPPost {
  id: number;
  content: string;
  imageURL: string;
  createdAt: number;
  ownerId: number;
  ownerName: string;
  ownerPhotoURL: string;
  category: string;
  subCategory: string;
  type: 'post' | 'match';
  matchData?: MatchData;
  hashtags: string[];
  likes: number[];
  likeCount: number;
  commentCount: number;
  shareCount: number;
  comments?: WPComment[];
}

export interface MatchData {
  players: { name: string; score: number }[];
  winner?: string;
}

export interface WPComment {
  id: number;
  content: string;
  authorName: string;
  authorPhoto: string;
  authorId: number;
  createdAt: number;
}

export interface WPUser {
  id: number;
  uid: number;
  displayName: string;
  email?: string;
  photoURL: string;
  bio?: string;
  postsCount?: number;
  createdAt: number;
}

export interface FeedResponse {
  posts: WPPost[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

let wpCredentials: { username: string; password: string } | null = null;

function parseJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch {
    return null;
  }
}

// Lấy token từ localStorage
function getToken(): string | null {
  return null;
}

function getWpCredentials(): { username: string; password: string } {
  localStorage.removeItem('wp_username');
  localStorage.removeItem('wp_password');
  localStorage.removeItem('wp_auth_token');
  return wpCredentials || { username: '', password: '' };
}

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${maxAgeSeconds};SameSite=Lax`;
}

function clearCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
}

export function persistFirebaseAuthState(uid: string, email = ''): void {
  localStorage.setItem('fb_uid', uid);
  localStorage.setItem('fb_email', email);
  setCookie('fb_uid', uid, 30 * 24 * 60 * 60);
  setCookie('fb_email', email, 30 * 24 * 60 * 60);
}

export function clearFirebaseAuthState(): void {
  localStorage.removeItem('fb_uid');
  localStorage.removeItem('fb_email');
  clearCookie('fb_uid');
  clearCookie('fb_email');
}

// Lấy Firebase UID (nếu đăng nhập bằng Firebase)
function getFirebaseUid(): string | null {
  // Ưu tiên localStorage, sau đó check cookie
  const localUid = localStorage.getItem('fb_uid');
  if (localUid) return localUid;
  
  // Check cookie
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'fb_uid') return value;
  }
  return null;
}

// Lưu Firebase UID vào cookie (expires trong 30 ngày)
function setFirebaseUidCookie(uid: string): void {
  setCookie('fb_uid', uid, 30 * 24 * 60 * 60);
}

// Xóa Firebase UID cookie
function clearFirebaseUidCookie(): void {
  clearCookie('fb_uid');
}

// Token Firebase do app native bơm vào (web dùng thẳng, không cần phiên Google).
// Lưu localStorage để sống sót qua reload trong WebView.
const FB_INJECTED_TOKEN_KEY = 'fb_injected_token';
const FB_INJECTED_EXP_KEY = 'fb_injected_token_exp';

export function setInjectedFirebaseToken(token: string): void {
  try {
    localStorage.setItem(FB_INJECTED_TOKEN_KEY, token);
    // Token Firebase sống ~1h, dùng tối đa 55 phút rồi chờ app bơm lại
    localStorage.setItem(FB_INJECTED_EXP_KEY, String(Date.now() + 55 * 60 * 1000));
  } catch {
    // localStorage không khả dụng -> bỏ qua
  }
}

function getInjectedFirebaseToken(): string | null {
  try {
    const exp = parseInt(localStorage.getItem(FB_INJECTED_EXP_KEY) || '0', 10);
    if (Date.now() > exp) return null;
    return localStorage.getItem(FB_INJECTED_TOKEN_KEY);
  } catch {
    return null;
  }
}

// Cầu nối sang app native: khi token Firebase hết hạn nhưng UID vẫn còn
// (người dùng vẫn "đăng nhập" theo UI), web xin app bơm lại token mới rồi thử lại.
// App bơm xong sẽ dispatch `vncues:auth-refreshed` để web tiếp tục.
let authRefreshResolver: (() => void) | null = null;
let authRefreshBound = false;

function bindAuthRefreshListener() {
  if (authRefreshBound) return;
  authRefreshBound = true;
  window.addEventListener('vncues:auth-refreshed', () => {
    authRefreshResolver?.();
  });
}

function requestAuthRefreshFromNative(timeoutMs = 8000): Promise<void> {
  return new Promise((resolve) => {
    const w = window as any;
    if (!w.ReactNativeWebView?.postMessage) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      authRefreshResolver = null;
      resolve();
    }, timeoutMs);
    authRefreshResolver = () => {
      clearTimeout(timer);
      authRefreshResolver = null;
      resolve();
    };
    w.ReactNativeWebView.postMessage(JSON.stringify({ type: 'request-auth' }));
  });
}

async function getFirebaseAuthHeaders(): Promise<Record<string, string>> {
  let token = (await getIdToken()) || getInjectedFirebaseToken();
  const uid = getFirebaseUid();
  if (!token && uid) {
    // Token bị hết hạn (UI vẫn đang "đã đăng nhập") -> xin app bơm token mới
    bindAuthRefreshListener();
    await requestAuthRefreshFromNative();
    token = (await getIdToken()) || getInjectedFirebaseToken();
  }
  if (!token || !uid) return {};
  return {
    'X-Firebase-Token': token,
    'X-Firebase-UID': uid,
  };
}

async function wpRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const firebaseUid = getFirebaseUid();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  if (firebaseUid) {
    (headers as Record<string, string>)['X-Firebase-UID'] = firebaseUid;
  }
  
  const response = await fetch(`${WP_API_BASE_CORE}${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  
  return response.json();
}

// VNCues custom API requests (uses vncues/v1 namespace)
async function vncuesRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const { username, password } = getWpCredentials();
  
  const firebaseHeaders = await getFirebaseAuthHeaders();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...firebaseHeaders,
    ...(options.headers || {}),
  };
  
  if (username && password) {
    (headers as Record<string, string>)['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  
  const response = await fetch(`${WP_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  
  return response.json();
}

export async function login(username: string, password: string): Promise<{ token: string; user: WPUser }> {
  // Sử dụng WordPress REST API với Application Password
  const response = await fetch(`${WP_API_BASE_CORE}/users/me`, {
    method: 'GET',
    headers: {
      'Authorization': 'Basic ' + btoa(`${username}:${password}`),
    },
  });
  
  if (!response.ok) {
    throw new Error('Đăng nhập thất bại. Vui lòng kiểm tra tên đăng nhập và mật khẩu ứng dụng.');
  }
  
  const userData = await response.json();
  
  // Tạo JWT-like token (trong thực tế nên dùng JWT thật)
  wpCredentials = { username, password };
  
  const user: WPUser = {
    id: userData.id,
    uid: userData.id,
    displayName: userData.name,
    email: userData.email,
    photoURL: userData.avatar_urls?.['96'] || '',
    bio: userData.description,
    createdAt: new Date(userData.registered_date).getTime(),
  };
  
  return { token: '', user };
}

export function logout(): void {
  wpCredentials = null;
  clearFirebaseAuthState();
}

export function isLoggedIn(): boolean {
  // Check WP token
  if (getToken()) return true;
  // Check Firebase UID
  if (getFirebaseUid()) return true;
  return false;
}

export function getCurrentUserId(): number | null {
  const token = getToken();
  if (!token) return null;
  
  const decoded = parseJWT(token);
  return decoded?.user_id || null;
}

export async function getCurrentUser(): Promise<WPUser> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  
  // Nếu có Firebase UID
  if (firebaseUid) {
    try {
      return await getCurrentUserWithFirebase(firebaseUid);
    } catch {
      // User Firebase chưa được link trong WP → thử link lại
      const { getCurrentFirebaseUser } = await import('./firebaseAuth');
      const fbUser = getCurrentFirebaseUser();
      if (fbUser) {
        const result = await linkFirebaseUser(
          fbUser.uid,
          fbUser.email || '',
          fbUser.displayName || '',
          fbUser.photoURL || ''
        );
        if (result.success && result.user) {
          setFirebaseUidCookie(fbUser.uid);
          return result.user;
        }
      }
      throw new Error('Firebase user not linked to WordPress');
    }
  }
  
  // Nếu có WP credentials
  if (!username || !password) {
    throw new Error('Chưa đăng nhập');
  }
  
  const response = await fetch(`${WP_API_BASE_CORE}/users/me`, {
    headers: {
      'Authorization': 'Basic ' + btoa(`${username}:${password}`),
    },
  });
  
  if (!response.ok) {
    throw new Error('Không thể lấy thông tin người dùng');
  }
  
  const userData = await response.json();
  
  return {
    id: userData.id,
    uid: userData.id,
    displayName: userData.name,
    email: userData.email,
    photoURL: userData.avatar_urls?.['96'] || '',
    bio: userData.description,
    createdAt: new Date(userData.registered_date).getTime(),
  };
}

export async function getFeedPosts(params: { page?: number; per_page?: number; hashtag?: string } = {}): Promise<FeedResponse> {
  const page = params.page || 1;
  const per_page = params.per_page || 20;
  
  // Sử dụng custom endpoint /feed thay vì /posts
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
  });
  
  if (params.hashtag) {
    queryParams.set('hashtag', params.hashtag.replace('#', ''));
  }
  
  const feedData: any = await vncuesRequest(`/feed?${queryParams.toString()}`);
  const responseHeaders = new Headers();
  
  // Transform feed data to our format
  const posts = feedData.posts || feedData.items || [];
  
  const transformedPosts: WPPost[] = posts.map((post: any) => {
    // Support both custom format and WordPress post format
    const author = post._embedded?.author?.[0] || { id: post.ownerId, name: post.ownerName, avatar_urls: { '96': post.ownerPhotoURL } };
    const featuredMedia = post._embedded?.['wp:featuredmedia']?.[0];
    const categories = post._embedded?.['wp:term']?.flat() || [];
    
    // Extract hashtags from content
    const content = post.content?.rendered?.replace(/<[^>]*>/g, '') || post.content || '';
    const hashtagRegex = /#(\w+)/g;
    const hashtags: string[] = [];
    let match;
    while ((match = hashtagRegex.exec(content)) !== null) {
      hashtags.push('#' + match[1]);
    }
    
    return {
      id: post.id,
      content: content,
      imageURL: featuredMedia?.source_url || post.imageURL || '',
      createdAt: new Date(post.date || post.createdAt).getTime(),
      ownerId: author.id || post.ownerId || 0,
      ownerName: author.name || post.ownerName || 'Unknown',
      ownerPhotoURL: author.avatar_urls?.['96'] || post.ownerPhotoURL || '',
      category: categories.find((c: any) => c.taxonomy === 'category')?.name || post.category || 'General',
      subCategory: categories.find((c: any) => c.taxonomy === 'category')?.name || post.subCategory || 'General',
      type: 'post' as const,
      hashtags: [...new Set(hashtags.length ? hashtags : (post.hashtags || []))],
      likes: post.likes || [],
      likeCount: post.likeCount || post.likes?.length || 0,
      commentCount: post.commentCount || post.comment_count || 0,
      shareCount: post.shareCount || 0,
    };
  });
  
  return {
    posts: transformedPosts,
    total: feedData.total || transformedPosts.length,
    page,
    per_page,
    has_more: feedData.has_more || transformedPosts.length === per_page,
  };
}

export async function getPost(postId: number): Promise<WPPost | null> {
  try {
    const post: any = await vncuesRequest(`/posts/${postId}`);
    
    return {
      id: post.id,
      content: post.content || '',
      imageURL: post.imageURL || '',
      createdAt: new Date(post.createdAt).getTime(),
      ownerId: post.ownerId || 0,
      ownerName: post.ownerName || 'Unknown',
      ownerPhotoURL: post.ownerPhotoURL || '',
      category: post.category || 'General',
      subCategory: post.subCategory || 'General',
      type: 'post',
      hashtags: post.hashtags || [],
      likes: post.likes || [],
      likeCount: post.likeCount || 0,
      commentCount: post.commentCount || 0,
      shareCount: post.shareCount || 0,
    };
  } catch {
    return null;
  }
}

export async function createPost(data: { content: string; category?: string; hashtags?: string[]; image_url?: string; image_id?: number }): Promise<WPPost> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  const firebaseHeaders = await getFirebaseAuthHeaders();
  
  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }
  
  // Tạo nội dung với hashtags
  let content = data.content;
  if (data.hashtags && data.hashtags.length > 0) {
    content += '\n\n' + data.hashtags.join(' ');
  }
  
  // Build headers - dùng Firebase UID hoặc Basic Auth
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  let userId: number | null = null;
  let userName: string = 'Unknown';
  
  if (firebaseUid) {
    Object.assign(headers, firebaseHeaders);
    // Lấy user info từ Firebase
    try {
      const fbUser = await getCurrentUserWithFirebase(firebaseUid);
      userId = fbUser.id;
      userName = fbUser.displayName || 'Unknown';
    } catch {
      // Thử lấy từ cached
      userId = getCurrentUserId();
      userName = getWpCredentials().username || 'Unknown';
    }
  } else {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
    userId = getCurrentUserId();
    userName = username || 'Unknown';
  }
  
  // GỌI ENDPOINT CỦA PLUGIN vncues/v1/posts (hỗ trợ X-Firebase-UID)
  const response = await fetch(`${WP_API_BASE}/posts`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      content,
      category: data.category,
      hashtags: data.hashtags,
      image_url: data.image_url,
      image_id: data.image_id,
    }),
  });
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.code || 'Không thể tạo bài viết');
  }
  
  const post = await response.json();
  
  return {
    id: post.id,
    content: content,
    imageURL: data.image_url || '',
    createdAt: new Date(post.createdAt || Date.now()).getTime(),
    ownerId: userId || post.ownerId || 0,
    ownerName: userName || post.ownerName || 'Unknown',
    ownerPhotoURL: post.ownerPhotoURL || '',
    category: data.category || 'General',
    subCategory: data.category || 'General',
    type: 'post',
    hashtags: data.hashtags || [],
    likes: [],
    likeCount: 0,
    commentCount: 0,
    shareCount: 0,
  };
}

async function getCategoryId(categoryName: string): Promise<number> {
  const categories: any[] = await wpRequest('/categories?search=' + encodeURIComponent(categoryName));
  
  if (categories.length > 0) {
    return categories[0].id;
  }
  
  // Tạo category mới nếu chưa có
  const newCategory: any = await wpRequest('/categories', {
    method: 'POST',
    body: JSON.stringify({ name: categoryName }),
  });
  
  return newCategory.id;
}

export async function toggleLike(postId: number): Promise<{ liked: boolean; like_count: number }> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  
  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());
  
  const response = await fetch(`${WP_API_BASE}/posts/${postId}/like`, {
    method: 'POST',
    headers,
  });
  
  if (!response.ok) {
    throw new Error('Không thể thực hiện');
  }
  
  return response.json();
}

export async function getComments(postId: number): Promise<WPComment[]> {
  const comments: any[] = await wpRequest(`/comments?post=${postId}`);
  
  return comments.map((comment: any) => ({
    id: comment.id,
    content: comment.content?.rendered?.replace(/<[^>]*>/g, '') || '',
    authorName: comment.author_name,
    authorPhoto: comment.author_avatar_urls?.['96'] || '',
    authorId: comment.author,
    createdAt: new Date(comment.date).getTime(),
  }));
}

export async function addComment(postId: number, content: string): Promise<WPComment> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  
  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());
  
  const response = await fetch(`${WP_API_BASE}/posts/${postId}/comments`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ content }),
  });
  
  if (!response.ok) {
    throw new Error('Không thể thêm bình luận');
  }
  
  const comment = await response.json();
  
  return {
    id: comment.id,
    content: comment.content || content,
    authorName: comment.authorName || username || 'Unknown',
    authorPhoto: comment.authorPhoto || '',
    authorId: comment.authorId || getCurrentUserId() || 0,
    createdAt: comment.createdAt || Date.now(),
  };
}

export async function deletePost(postId: number): Promise<{ deleted: boolean; id: number }> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();

  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());

  const response = await fetch(`${WP_API_BASE}/posts/${postId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Không thể xóa bài viết');
  }

  return response.json();
}

export async function reportPost(postId: number, reason?: string): Promise<{ reported: boolean; report_count: number }> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();

  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());

  const response = await fetch(`${WP_API_BASE}/posts/${postId}/report`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Không thể báo cáo bài viết');
  }

  return response.json();
}

export async function getUserProfile(userId: number): Promise<WPUser> {
  const userData: any = await wpRequest(`/users/${userId}`);
  
  return {
    id: userData.id,
    uid: userData.id,
    displayName: userData.name,
    email: userData.email,
    photoURL: userData.avatar_urls?.['96'] || '',
    bio: userData.description,
    createdAt: new Date(userData.registered_date).getTime(),
  };
}

export async function getTrendingHashtags(): Promise<string[]> {
  // Lấy tất cả posts và extract hashtags
  const posts: any[] = await wpRequest('/posts?per_page=50&_fields=content');
  
  const hashtagCount: Record<string, number> = {};
  
  posts.forEach((post: any) => {
    const hashtagRegex = /#(\w+)/g;
    let match;
    while ((match = hashtagRegex.exec(post.content?.rendered || '')) !== null) {
      const tag = '#' + match[1];
      hashtagCount[tag] = (hashtagCount[tag] || 0) + 1;
    }
  });
  
  return Object.entries(hashtagCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([tag]) => tag);
}

export async function sharePost(postId: number): Promise<{ share_count: number }> {
  // WordPress không có native share, tạm thời lưu vào localStorage
  const metaKey = `post_shares_${postId}`;
  const shares = parseInt(localStorage.getItem(metaKey) || '0') + 1;
  localStorage.setItem(metaKey, shares.toString());
  
  return { share_count: shares };
}

// ====== FOLLOW/UNFOLLOW ======

export async function followUser(userId: number): Promise<{ following: boolean; followers_count: number; following_count: number }> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  
  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());
  
  const response = await fetch(`${WP_API_BASE}/users/${userId}/follow`, {
    method: 'POST',
    headers,
  });
  
  if (!response.ok) {
    throw new Error('Không thể theo dõi người dùng');
  }
  
  return response.json();
}

export async function unfollowUser(userId: number): Promise<{ following: boolean; followers_count: number; following_count: number }> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  
  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());
  
  const response = await fetch(`${WP_API_BASE}/users/${userId}/unfollow`, {
    method: 'POST',
    headers,
  });
  
  if (!response.ok) {
    throw new Error('Không thể hủy theo dõi người dùng');
  }
  
  return response.json();
}

export async function getFollowers(userId: number): Promise<{ followers: WPUser[]; count: number }> {
  const response = await fetch(`${WP_API_BASE}/users/${userId}/followers`);
  
  if (!response.ok) {
    throw new Error('Không thể lấy danh sách người theo dõi');
  }
  
  return response.json();
}

export async function getFollowing(userId: number): Promise<{ following: WPUser[]; count: number }> {
  const response = await fetch(`${WP_API_BASE}/users/${userId}/following`);
  
  if (!response.ok) {
    throw new Error('Không thể lấy danh sách người đang theo dõi');
  }
  
  return response.json();
}

export async function getRelationship(userId: number): Promise<{ is_following: boolean; is_followed_by: boolean }> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  
  if (!username && !password && !firebaseUid) {
    return { is_following: false, is_followed_by: false };
  }
  
  const headers: Record<string, string> = {};
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());
  
  const response = await fetch(`${WP_API_BASE}/users/${userId}/relationship`, {
    headers,
  });
  
  if (!response.ok) {
    return { is_following: false, is_followed_by: false };
  }
  
  return response.json();
}

// ====== IMAGE UPLOAD ======

export async function uploadImage(file: File): Promise<{ id: number; url: string; thumbnail: string; medium: string; full: string }> {
  const { username, password } = getWpCredentials();
  const firebaseUid = getFirebaseUid();
  
  if (!username && !password && !firebaseUid) {
    throw new Error('Chưa đăng nhập');
  }
  
  const formData = new FormData();
  formData.append('file', file);
  
  const headers: Record<string, string> = {};
  if (username && password) {
    headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
  }
  Object.assign(headers, await getFirebaseAuthHeaders());
  
  const response = await fetch(`${WP_API_BASE}/media/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(error.message || 'Không thể tải lên ảnh');
  }
  
  return response.json();
}

// Register new user
export async function register(
  username: string, 
  email: string, 
  password: string
): Promise<{ success: boolean; user?: WPUser; message?: string }> {
  try {
    // Use VNCues custom API to create user
    const response = await fetch(`${WP_API_BASE}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        email,
        password,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      const message = error.message || error.code || 'Đăng ký thất bại';
      throw new Error(Array.isArray(message) ? message[0] : message);
    }
    
    const userData = await response.json();
    
    return {
      success: true,
      user: {
        id: userData.id,
        uid: userData.uid || userData.id,
        displayName: userData.displayName || username,
        email: userData.email || email,
        photoURL: userData.photoURL || '',
        createdAt: Date.now(),
      },
      message: 'Đăng ký thành công! Vui lòng đăng nhập.',
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Đăng ký thất bại',
    };
  }
}

// Export as wpApi object for compatibility
export const wpApi = {
  login,
  logout,
  register,
  getCurrentUser,
  getFeedPosts,
  getPost,
  createPost,
  toggleLike,
  getComments,
  addComment,
  getUserProfile,
  getTrendingHashtags,
  sharePost,
  deletePost,
  reportPost,
  isLoggedIn,
  getCurrentUserId,
  // New features
  followUser,
  unfollowUser,
  getFollowers,
  getFollowing,
  getRelationship,
  uploadImage,
  // Firebase Auth
  linkFirebaseUser,
  getCurrentUserWithFirebase,
  persistFirebaseAuthState,
  clearFirebaseAuthState,
  setInjectedFirebaseToken,
};

// ============================================
// FIREBASE AUTH INTEGRATION
// ============================================

/**
 * Link Firebase user to WordPress user
 * Creates WordPress user if not exists
 */
export async function linkFirebaseUser(
  firebaseUid: string,
  email: string,
  displayName?: string,
  photoURL?: string,
  overrideToken?: string
): Promise<{ success: boolean; user?: WPUser; message?: string }> {
  try {
    const firebaseToken = overrideToken || (await getIdToken());
    if (!firebaseToken) {
      throw new Error('Phiên Firebase không hợp lệ hoặc đã hết hạn');
    }

    const response = await fetch(`${WP_API_BASE}/firebase/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-Token': firebaseToken,
        'X-Firebase-UID': firebaseUid,
      },
      body: JSON.stringify({
        firebaseUid,
        email,
        displayName,
        photoURL,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.code || 'Link failed');
    }

    const data = await response.json();

    return {
      success: true,
      user: data.wp_user,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Link failed',
    };
  }
}

/**
 * Get current WordPress user using Firebase token
 */
export async function getCurrentUserWithFirebase(firebaseUid: string): Promise<WPUser> {
  const firebaseToken = (await getIdToken()) || getInjectedFirebaseToken();
  if (!firebaseToken) {
    throw new Error('Phiên Firebase không hợp lệ hoặc đã hết hạn');
  }

  const response = await fetch(`${WP_API_BASE}/firebase/me`, {
    method: 'GET',
    headers: {
      'X-Firebase-Token': firebaseToken,
      'X-Firebase-UID': firebaseUid,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get user');
  }

  return response.json();
}
